import { memo, useCallback, useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import { useClickOutside, useDisclosure } from '@aimsos/hooks';
import Badge from './Badge.jsx';
import Button from './Button.jsx';
import MenuItem from './Menu/MenuItem.jsx';
import MenuPanel from './Menu/MenuPanel.jsx';
import {
  MoreVertical,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Pencil,
  Search,
  X,
} from 'lucide-react';

// ─── Shared tokens ────────────────────────────────────────────────────────────

const SIZE_CONFIG = {
  M: {
    iconBoxClass: 'h-10 w-10 min-h-10 min-w-10 rounded-lg p-2',
    iconSize: 24,
    titleClass: 'text-[24px] leading-[29px] font-semibold tracking-[0.25px]',
    subtitleClass: 'text-[14px] leading-[20px] font-medium',
    tabTextClass: 'text-[14px] leading-[20px] font-semibold',
  },
  S: {
    iconBoxClass: 'h-8 w-8 min-h-8 min-w-8 rounded-lg p-2',
    iconSize: 16,
    titleClass: 'text-[18px] leading-[24px] font-semibold tracking-[0.25px]',
    subtitleClass: 'text-[12px] leading-[20px] font-medium',
    tabTextClass: 'text-[14px] leading-[20px] font-semibold',
  },
};

const COLLAPSE_TITLE_CLASS = 'text-[18px] leading-normal font-semibold tracking-[0.25px]';
const COLLAPSE_SUBTITLE_CLASS = 'text-[14px] leading-5 font-medium';

const ACTION_BTN_CLASS = [
  'shrink-0 flex items-center justify-center w-[28px] h-[28px] rounded p-1',
  'text-[#52525B] dark:text-[#D1D5DB] hover:bg-black/5 dark:hover:bg-white/10 transition-colors',
].join(' ');

const join = (...parts) => parts.filter(Boolean).join(' ');

const resolveToggleIcon = (position, isCollapsed) => {
  if (position === 'left') return isCollapsed ? PanelLeftOpen : PanelLeftClose;
  return isCollapsed ? PanelRightOpen : PanelRightClose;
};

// ─── Panel collapse toggle (shared with SlideOut) ─────────────────────────────

/** Expand/collapse control — used by header `variant="collapse"` and SlideOut rail. */
export const PanelCollapseToggle = memo(({
  isCollapsed,
  position,
  collapseAriaLabel,
  expandAriaLabel,
  onToggle,
  iconSize = 16,
}) => {
  const ToggleIcon = resolveToggleIcon(position, isCollapsed);

  return (
    <button
      type="button"
      aria-label={isCollapsed ? expandAriaLabel : collapseAriaLabel}
      aria-expanded={!isCollapsed}
      onClick={onToggle}
      className={ACTION_BTN_CLASS}
    >
      <ToggleIcon size={iconSize} />
    </button>
  );
});

PanelCollapseToggle.displayName = 'PanelCollapseToggle';
PanelCollapseToggle.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  position: PropTypes.oneOf(['left', 'right']).isRequired,
  collapseAriaLabel: PropTypes.string.isRequired,
  expandAriaLabel: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  iconSize: PropTypes.number,
};

// ─── Overflow menu ──────────────────────────────────────────────────────────────

const MenuOptionRow = memo(({ option, onSelected }) => {
  const handleClick = useCallback(() => {
    if (option.disabled) return;
    option.action?.();
    onSelected();
  }, [onSelected, option]);

  return (
    <MenuItem
      size="small"
      label={option.name}
      icon={option.icon || false}
      disabled={option.disabled}
      onClick={handleClick}
    />
  );
});

MenuOptionRow.displayName = 'MenuOptionRow';
MenuOptionRow.propTypes = {
  option: PropTypes.shape({
    name: PropTypes.string.isRequired,
    action: PropTypes.func.isRequired,
    icon: PropTypes.elementType,
    disabled: PropTypes.bool,
  }).isRequired,
  onSelected: PropTypes.func.isRequired,
};

const HeaderOverflowMenu = memo(({ options, ariaLabel = 'More options' }) => {
  const menuId = useId();
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const { isOpen, toggle, close } = useDisclosure();

  useClickOutside(containerRef, close);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
      triggerRef.current?.focus();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close, isOpen]);

  if (!Array.isArray(options) || options.length === 0) return null;

  return (
    <div ref={containerRef} className="relative shrink-0">
      <span ref={triggerRef} className="inline-flex">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          leftIcon={MoreVertical}
          customIconSize={16}
          aria-label={ariaLabel}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-controls={menuId}
          onClick={toggle}
          className="!h-7 !w-7 !min-h-7 !min-w-7 !p-1 text-[#52525B] dark:text-[#D1D5DB]"
        />
      </span>

      <div
        id={menuId}
        aria-hidden={!isOpen || undefined}
        className={join(
          'absolute right-0 top-full z-[1000] mt-2',
          !isOpen && 'invisible pointer-events-none',
        )}
      >
        <MenuPanel role="menu" ariaLabel={ariaLabel} className="w-[16.25rem]">
          {options.map((option) => (
            <MenuOptionRow key={option.name} option={option} onSelected={close} />
          ))}
        </MenuPanel>
      </div>
    </div>
  );
});

HeaderOverflowMenu.displayName = 'HeaderOverflowMenu';
HeaderOverflowMenu.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      action: PropTypes.func.isRequired,
      icon: PropTypes.elementType,
      disabled: PropTypes.bool,
    }),
  ),
  ariaLabel: PropTypes.string,
};

// ─── Variant sub-components ───────────────────────────────────────────────────

const HeaderIconBox = memo(({ Icon, sizeKey, collapseTone = false }) => {
  const cfg = SIZE_CONFIG[sizeKey] ?? SIZE_CONFIG.M;
  return (
    <div
      className={join(
        'flex shrink-0 items-center justify-center',
        collapseTone ? 'bg-[#F3E9FD] dark:bg-[#2173FF]/15' : 'bg-[#F3E9FD]',
        cfg.iconBoxClass,
      )}
    >
      <Icon
        size={cfg.iconSize}
        className={collapseTone ? 'text-[#7B3FC4] dark:text-[#80AFFF]' : 'text-[#7B3FC4]'}
      />
    </div>
  );
});

HeaderIconBox.displayName = 'HeaderIconBox';
HeaderIconBox.propTypes = {
  Icon: PropTypes.elementType.isRequired,
  sizeKey: PropTypes.oneOf(['M', 'S']).isRequired,
  collapseTone: PropTypes.bool,
};

const CollapseHeaderRail = memo(({
  className,
  position,
  collapseAriaLabel,
  expandAriaLabel,
  onCollapseToggle,
}) => (
  <div className={join('flex w-full shrink-0 justify-center', className)}>
    <PanelCollapseToggle
      isCollapsed
      position={position}
      collapseAriaLabel={collapseAriaLabel}
      expandAriaLabel={expandAriaLabel}
      onToggle={onCollapseToggle}
    />
  </div>
));

CollapseHeaderRail.displayName = 'CollapseHeaderRail';

const CollapseHeader = memo(({
  title,
  titleId,
  subtitle,
  icon: Icon,
  size,
  className,
  menuOptions,
  menuAriaLabel,
  isCollapsed,
  position,
  collapseAriaLabel,
  expandAriaLabel,
  onCollapseToggle,
}) => (
  <div className={join('flex w-full items-start gap-2', className)}>
    {Icon && <HeaderIconBox Icon={Icon} sizeKey={size} collapseTone />}

    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <h2 id={titleId} className={join(COLLAPSE_TITLE_CLASS, 'text-[#000000] dark:text-[#F8FAFC]')}>
        {title}
      </h2>
      {subtitle && (
        <p className={join(COLLAPSE_SUBTITLE_CLASS, 'text-[#5C5C5C] dark:text-[#94A3B8]')}>
          {subtitle}
        </p>
      )}
    </div>

    <div className="flex shrink-0 items-center gap-1">
      {Array.isArray(menuOptions) && menuOptions.length > 0 && (
        <HeaderOverflowMenu options={menuOptions} ariaLabel={menuAriaLabel} />
      )}
      {onCollapseToggle && (
        <PanelCollapseToggle
          isCollapsed={isCollapsed}
          position={position}
          collapseAriaLabel={collapseAriaLabel}
          expandAriaLabel={expandAriaLabel}
          onToggle={onCollapseToggle}
        />
      )}
    </div>
  </div>
));

CollapseHeader.displayName = 'CollapseHeader';

const HeaderTab = memo(({ tab, isActive, tabTextClass, onTabChange }) => (
  <button
    role="tab"
    aria-selected={isActive}
    type="button"
    onClick={() => onTabChange?.(tab.value)}
    className={join(
      'relative flex flex-1 items-center justify-center px-4 py-2',
      tabTextClass,
      isActive ? 'text-[#2173FF]' : 'text-[#2A2A2A] dark:text-[#94A3B8]',
    )}
  >
    {tab.label}
    {isActive && (
      <span className="absolute bottom-0 left-0 right-0 h-[2px] rounded-full bg-[#2173FF]" />
    )}
  </button>
));

HeaderTab.displayName = 'HeaderTab';
HeaderTab.propTypes = {
  tab: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  isActive: PropTypes.bool.isRequired,
  tabTextClass: PropTypes.string.isRequired,
  onTabChange: PropTypes.func,
};

const DefaultHeader = memo(({
  title,
  titleId,
  subtitle,
  icon: Icon,
  status,
  showEdit,
  showClose,
  onEdit,
  onClose,
  tabs,
  activeTab,
  onTabChange,
  showSearch,
  searchValue = '',
  onSearchChange,
  size,
  className,
}) => {
  const cfg = SIZE_CONFIG[size] ?? SIZE_CONFIG.M;
  const hasTabs = Array.isArray(tabs) && tabs.length > 0;

  return (
    <div className={join('flex w-full flex-col gap-3', className)}>
      <div className="flex w-full items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          {Icon && <HeaderIconBox Icon={Icon} sizeKey={size} />}

          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2
                id={titleId}
                className={join(cfg.titleClass, 'whitespace-nowrap text-[#000000] dark:text-[#F8FAFC]')}
              >
                {title}
              </h2>
              {status && (
                <Badge variant="success" size="medium">
                  {status}
                </Badge>
              )}
            </div>

            {subtitle && (
              <p className={join(cfg.subtitleClass, 'text-[#5C5C5C] dark:text-[#94A3B8]')}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {showEdit && (
            <button type="button" onClick={onEdit} className={ACTION_BTN_CLASS} aria-label="Edit">
              <Pencil size={16} />
            </button>
          )}
          {showClose && (
            <button type="button" onClick={onClose} className={ACTION_BTN_CLASS} aria-label="Close panel">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {hasTabs && (
        <nav role="tablist" className="flex w-full items-start">
          {tabs.map((tab) => (
            <HeaderTab
              key={tab.value}
              tab={tab}
              isActive={tab.value === activeTab}
              tabTextClass={cfg.tabTextClass}
              onTabChange={onTabChange}
            />
          ))}
        </nav>
      )}

      {showSearch && (
        <div className="flex h-8 w-full items-center gap-1 rounded-lg border-[0.5px] border-[#5C5C5C] bg-white px-4 py-1 dark:border-white/15 dark:bg-[#1E2535]">
          <Search size={16} className="shrink-0 text-[#52525B] dark:text-[#D1D5DB]" />
          <input
            type="text"
            {...(onSearchChange
              ? { value: searchValue, onChange: (e) => onSearchChange(e.target.value) }
              : { defaultValue: searchValue }
            )}
            placeholder="Search"
            className="min-w-0 flex-1 bg-transparent text-[14px] font-medium leading-5 text-[#2A2A2A] placeholder-[#BABABA] focus:outline-none dark:text-[#F8FAFC]"
          />
        </div>
      )}
    </div>
  );
});

DefaultHeader.displayName = 'DefaultHeader';

// ─── Public component ─────────────────────────────────────────────────────────

/**
 * Header for {@link SlideOut}. Supports a standard layout (`variant="default"`) and a
 * compact collapsible layout (`variant="collapse"`) aligned with the Node Library Figma.
 *
 * When used with `<SlideOut variant="collapsible">` and `variant="collapse"`, the panel
 * injects collapse state via `cloneElement` — no need to pass `onCollapseToggle` manually.
 * Search belongs in `SlideOut` children, not in the collapse header.
 *
 * @param {object} props
 * @param {'default'|'collapse'} [props.variant='default']
 * @param {string} props.title
 * @param {string} [props.titleId]
 * @param {string} [props.subtitle]
 * @param {import('lucide-react').LucideIcon} [props.icon]
 * @param {Array<{ name: string, action: Function, icon?: import('lucide-react').LucideIcon, disabled?: boolean }>} [props.menuOptions]
 * @param {boolean} [props.isCollapsed] — Collapse variant; injected by SlideOut when collapsed.
 * @param {Function} [props.onCollapseToggle] — Collapse variant; injected by SlideOut.
 * @param {'left'|'right'} [props.position='right']
 */
const SlideOutHeader = ({
  variant = 'default',
  isCollapsed = false,
  ...props
}) => {
  if (variant === 'collapse') {
    if (isCollapsed) {
      return (
        <CollapseHeaderRail
          className={props.className}
          position={props.position}
          collapseAriaLabel={props.collapseAriaLabel}
          expandAriaLabel={props.expandAriaLabel}
          onCollapseToggle={props.onCollapseToggle}
        />
      );
    }
    return <CollapseHeader isCollapsed={isCollapsed} {...props} />;
  }

  return <DefaultHeader {...props} />;
};

SlideOutHeader.propTypes = {
  title: PropTypes.string.isRequired,
  titleId: PropTypes.string,
  subtitle: PropTypes.string,
  icon: PropTypes.elementType,
  status: PropTypes.string,
  showEdit: PropTypes.bool,
  showClose: PropTypes.bool,
  onEdit: PropTypes.func,
  onClose: PropTypes.func,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  showSearch: PropTypes.bool,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  size: PropTypes.oneOf(['M', 'S']),
  className: PropTypes.string,
  /** `default` — header estándar. `collapse` — título compacto + toggle colapsar + menú opcional. */
  variant: PropTypes.oneOf(['default', 'collapse']),
  /** Solo `variant="collapse"`. Inyectado por SlideOut cuando el panel está colapsado. */
  isCollapsed: PropTypes.bool,
  /** Solo `variant="collapse"`. Callback del toggle colapsar/expandir. */
  onCollapseToggle: PropTypes.func,
  /** Solo `variant="collapse"`. Alineación del icono de panel (`left` | `right`). */
  position: PropTypes.oneOf(['left', 'right']),
  collapseAriaLabel: PropTypes.string,
  expandAriaLabel: PropTypes.string,
  /** Menú overflow (tres puntos). Array de `{ name, action, icon?, disabled? }`. */
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      action: PropTypes.func.isRequired,
      icon: PropTypes.elementType,
      disabled: PropTypes.bool,
    }),
  ),
  menuAriaLabel: PropTypes.string,
};

export default memo(SlideOutHeader);
